// Fetches the product's animated WebP once the card nears the viewport on desktop,
// then gives the <img> a new blob URL on each hover. A browser restarts an animated
// image only for a URL it hasn't seen, so this replays from frame one with no
// re-download. Touch and reduced-motion visitors never fetch the file.
class ProductCardHover extends HTMLElement {
  connectedCallback() {
    this.img = this.querySelector("img");
    this.card = this.closest(".product-card");
    if (!this.img || !this.card || this.blob) return;

    const query =
      "(hover: hover) and (pointer: fine) and (min-width: 1025px) and (prefers-reduced-motion: no-preference)";
    if (!window.matchMedia(query).matches) return;

    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.onLoad = this.onLoad.bind(this);

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: "200px" }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.card?.removeEventListener("mouseenter", this.onEnter);
    this.card?.removeEventListener("mouseleave", this.onLeave);
    this.img?.removeEventListener("load", this.onLoad);
    this.clear();
  }

  // Without an explicit Accept header Shopify's CDN answers with a GIF conversion.
  async load() {
    const src =
      window.devicePixelRatio > 1.25 ? this.dataset.srcHd : this.dataset.src;
    try {
      const response = await fetch(src, { headers: { Accept: "image/webp" } });
      if (!response.ok) return;
      const blob = await response.blob();
      if (blob.type !== "image/webp") return;
      this.blob = blob;
    } catch {
      return;
    }
    this.img.addEventListener("load", this.onLoad);
    this.card.addEventListener("mouseenter", this.onEnter);
    this.card.addEventListener("mouseleave", this.onLeave);
    if (this.card.matches(":hover")) this.onEnter();
  }

  onEnter() {
    clearTimeout(this.clearTimer);
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = URL.createObjectURL(this.blob);
    this.img.src = this.url;
  }

  onLoad() {
    if (this.card.matches(":hover")) {
      this.card.classList.add("product-card--hover-playing");
    }
  }

  // Drops the src once the fade-out ends so the animation stops decoding off-screen.
  onLeave() {
    this.card.classList.remove("product-card--hover-playing");
    clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => this.clear(), 400);
  }

  clear() {
    this.img?.removeAttribute("src");
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = null;
  }
}

if (!customElements.get("product-card-hover")) {
  customElements.define("product-card-hover", ProductCardHover);
}
